// src/components/shared/SharedTable.tsx
// All comments in English.
import React, { useMemo, useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type SortDir = "asc" | "desc";

export type SharedTableColumn<T = any> = {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  sortType?: "string" | "number" | "date" | "custom";
  getValue?: (row: T) => any;
  compareFn?: (a: any, b: any) => number;
  widthClassName?: string;
};

export type SharedTableProps<T = any> = {
  rows: T[];
  cols: SharedTableColumn<T>[];
  emptyText?: string;
  defaultSort?: { key: string; dir: SortDir };
  onSortChange?: (sort: { key: string; dir: SortDir } | null) => void;
  className?: string;
  onRowClick?: (row: T) => void;
  rowTooltipContent?: (row: T) => React.ReactNode;
};

function getAutoType(v: any): "number" | "string" | "date" {
  if (v instanceof Date) return "date";
  if (typeof v === "number") return "number";
  if (typeof v === "string") {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return "date";
    return "string";
  }
  return "string";
}

function normalize(v: any) {
  if (v === null || v === undefined) return null;
  return v;
}

function defaultCompare(a: any, b: any, type: "string" | "number" | "date") {
  const na = normalize(a);
  const nb = normalize(b);
  if (na === null && nb === null) return 0;
  if (na === null) return 1;
  if (nb === null) return -1;

  if (type === "number") {
    const aa = Number(na);
    const bb = Number(nb);
    if (isNaN(aa) && isNaN(bb)) return 0;
    if (isNaN(aa)) return 1;
    if (isNaN(bb)) return -1;
    return aa - bb;
  }

  if (type === "date") {
    const ta = na instanceof Date ? na.getTime() : new Date(na).getTime();
    const tb = nb instanceof Date ? nb.getTime() : new Date(nb).getTime();
    if (isNaN(ta) && isNaN(tb)) return 0;
    if (isNaN(ta)) return 1;
    if (isNaN(tb)) return -1;
    return ta - tb;
  }

  return String(na).localeCompare(String(nb));
}

export default function SharedTable<T = any>({
  rows,
  cols,
  emptyText = "No data",
  defaultSort,
  onSortChange,
  className,
  onRowClick,
  rowTooltipContent,
}: SharedTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(
    defaultSort ?? null
  );

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = cols.find((c) => c.key === sort.key);
    if (!col) return rows;

    const { sortType, getValue, compareFn } = col;

    const type: "string" | "number" | "date" | "custom" =
      sortType ?? (() => {
        for (const r of rows) {
          const v = getValue ? getValue(r) : (r as any)[col.key];
          if (v !== null && v !== undefined) {
            return getAutoType(v);
          }
        }
        return "string";
      })();

    const dir = sort.dir === "asc" ? 1 : -1;

    const arr = [...rows];
    arr.sort((ra, rb) => {
      const va = getValue ? getValue(ra) : (ra as any)[col.key];
      const vb = getValue ? getValue(rb) : (rb as any)[col.key];

      if (type === "custom" && compareFn) {
        return dir * compareFn(va, vb);
      }

      const baseType = type === "custom" ? "string" : (type as "string" | "number" | "date");
      return dir * defaultCompare(va, vb, baseType);
    });
    return arr;
  }, [rows, cols, sort]);

  function handleHeaderClick(col: SharedTableColumn) {
    if (col.sortable === false) return;
    setSort((prev) => {
      if (!prev || prev.key !== col.key) {
        const next = { key: col.key, dir: "asc" as SortDir };
        onSortChange?.(next);
        return next;
      }
      const nextDir = prev.dir === "asc" ? "desc" : "asc";
      const next = { key: col.key, dir: nextDir };
      onSortChange?.(next);
      return next;
    });
  }

  const wrapperClass = ["overflow-hidden rounded-xl border bg-white", className]
    .filter(Boolean)
    .join(" ");

  return (
    <TooltipProvider delayDuration={150}>
      <div className={wrapperClass}>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50">
            <tr>
              {cols.map((c) => {
                const isSorted = sort?.key === c.key;
                const dirArrow =
                  isSorted && sort?.dir === "asc" ? "▲" : isSorted ? "▼" : "↕";
                const clickable = c.sortable !== false;

                return (
                  <th
                    key={c.key}
                    className={[
                      "px-4 py-2 font-medium text-gray-700",
                      c.widthClassName ?? "",
                      clickable ? "cursor-pointer select-none" : "",
                    ].join(" ")}
                    onClick={() => clickable && handleHeaderClick(c)}
                    title={clickable ? "Click to sort" : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <span>{c.header}</span>
                      <span className="text-xs text-gray-400">{dirArrow}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y">
            {sortedRows.length === 0 ? (
              <tr>
                <td className="px-4 py-4 text-gray-500" colSpan={cols.length}>
                  {emptyText}
                </td>
              </tr>
            ) : (
              sortedRows.map((r, idx) => {
                const rowNode = (
                  <tr
                    key={idx}
                    className={[
                      "hover:bg-gray-50/70",
                      onRowClick ? "cursor-pointer" : "",
                    ].join(" ")}
                    onClick={() => onRowClick?.(r)}
                    role={onRowClick ? "button" : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                  >
                    {cols.map((c) => (
                      <td
                        key={c.key}
                        className={[
                          "px-4 py-2 align-middle",
                          c.widthClassName ?? "",
                        ].join(" ")}
                      >
                        {c.render ? c.render(r) : (r as any)[c.key] ?? "-"}
                      </td>
                    ))}
                  </tr>
                );

                if (!rowTooltipContent) return rowNode;

                return (
                  <Tooltip key={idx}>
                    <TooltipTrigger asChild>{rowNode}</TooltipTrigger>
                    <TooltipContent className="max-w-sm rounded-lg border border-slate-200 bg-white p-3 text-slate-900 shadow-lg">
                      {rowTooltipContent(r)}
                    </TooltipContent>
                  </Tooltip>
                );
              })
            )}
          </tbody>
          </table>
        </div>
      </div>
    </TooltipProvider>
  );
}
